// reportSlots.js
// Photo slot storage — one slot per form item (see reportItems.js), plus the
// site systems checklist answers.
//
// Firestore: projects/{projectId}/forms/reportSlots
//   {
//     siteSystems: {
//       pool: { present: false, reason: 'no swimming pool on site' },
//       …
//     },
//     slots: {
//       item_27: {
//         photos: [                         // [0] = primary (PHOTO-27), [1] = PHOTO-27-1 …
//           { photoId: 'abc', findings: 'Repair damaged rooftop receptacles …' },
//           { photoId: 'def', findings: 'Remove extension cords …' },
//         ],
//         statuses: { mechanical_wiring: 'repair' },   // one per status line
//         lineComments: {},                  // item 28 only: per-line comments
//         details: { svc_voltage: '208/120V', panels: [ … ] },  // item-specific form fields
//         naReason: '',                      // set → N/A panel prints
//         naSource: '',                      // 'checklist' | 'engineer'
//         review: 'ai',                      // 'empty' | 'ai' | 'edited' | 'confirmed'
//                                            // any edit after Confirm → 'edited' again
//         aiNote: '',                        // AI flag shown to the engineer
//         confirmedBy, confirmedAt,
//         updatedAt, updatedBy,
//       },
//       …
//     }
//   }
//
// Each slot is written with a field-path update (slots.item_27), so saving one
// item never overwrites another.
//
// Photo Manager is the source of truth for status and findings. The Electrical
// Form shows them read-only (applyToFormData builds the form values).

import { doc, getDoc, setDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { getReportItems, LEAD_ITEMS } from './reportItems';

export const NO_DEFICIENCY_TEXT = 'No deficiency observed at time of inspection.';

const slotsRef = (projectId) => doc(db, 'projects', projectId, 'forms', 'reportSlots');

// ─── Shape helpers ────────────────────────────────────────────────────────────

export const emptySlot = () => ({
  photos: [], statuses: {}, lineComments: {}, details: {}, naReason: '', naSource: '',
  review: 'empty', aiNote: '', confirmedBy: null, confirmedAt: null,
});

const normalizeSlot = (raw) => ({
  photos: Array.isArray(raw?.photos)
    ? raw.photos.map(p => ({ photoId: p?.photoId || null, findings: p?.findings || '' }))
    : [],
  statuses:     raw?.statuses && typeof raw.statuses === 'object' ? { ...raw.statuses } : {},
  lineComments: raw?.lineComments && typeof raw.lineComments === 'object' ? { ...raw.lineComments } : {},
  details:      raw?.details && typeof raw.details === 'object' ? { ...raw.details } : {},
  naReason:     raw?.naReason || '',
  naSource:     raw?.naSource || '',
  review:       ['empty', 'ai', 'edited', 'confirmed'].includes(raw?.review) ? raw.review : 'empty',
  aiNote:       raw?.aiNote || '',
  confirmedBy:  raw?.confirmedBy || null,
  confirmedAt:  raw?.confirmedAt || null,
});

export const isNotApplicable = (slot) => !!(slot && slot.naReason && slot.naReason.trim());
export const isConfirmed     = (slot) => slot?.review === 'confirmed';

export const naFindingsText = (slot) =>
  `Not applicable — ${(slot.naReason || '').trim().replace(/\.$/, '')}.`;

// Any engineer edit sends the slot to 'edited' — including a confirmed slot,
// which must be confirmed again. Nothing changes after sign-off without a
// fresh Confirm.
const touched = (slot) => ({ ...slot, review: 'edited', confirmedBy: null, confirmedAt: null });

// ─── Findings text ────────────────────────────────────────────────────────────

const joinParagraph = (parts) =>
  parts
    .map(t => (t || '').trim())
    .filter(Boolean)
    .map(t => (/[.!?]$/.test(t) ? t : `${t}.`))
    .join(' ');

// Text that prints in the findings box(es) for an item, as one paragraph.
export const combinedFindings = (slot, item) => {
  if (!slot) return '';
  if (isNotApplicable(slot)) return naFindingsText(slot);
  if (item?.perLineComments) return joinParagraph(item.lines.map(l => slot.lineComments?.[l.id]));
  return joinParagraph(slot.photos.map(p => p.findings));
};

// ─── Slot → Electrical Form values ────────────────────────────────────────────
// Returns a copy of formData with each item's statuses and comment set from its
// slot. Used by the Electrical Form (read-only display) and PDF generation.

export const applyToFormData = (formData, county, slots) => {
  const out = { ...(formData || {}) };
  getReportItems(county).forEach(item => {
    const slot = slots?.[item.key];
    if (!slot) return;
    const na = isNotApplicable(slot);

    item.lines.forEach(line => {
      let status = slot.statuses?.[line.id] || '';
      if (na) status = line.opts.includes('na') ? 'na' : '';
      out[line.id] = { ...(out[line.id] || {}), status };
    });

    if (item.naKey) out[item.naKey] = { value: na };

    // Item-specific fields (service size, item 28 levels, …) — panels go separately
    Object.entries(slot.details || {}).forEach(([k, v]) => {
      if (k !== 'panels') out[k] = { value: v };
    });

    if (item.perLineComments) {
      item.lines.forEach(line => {
        out[line.id] = { ...(out[line.id] || {}), comment: na ? '' : (slot.lineComments?.[line.id] || '') };
      });
    } else if (item.commentKey) {
      out[item.commentKey] = { ...(out[item.commentKey] || {}), comment: combinedFindings(slot, item) };
    }
  });
  return out;
};

// True when the slot records a repair: any repair-type status, or (item 7) any
// panel marked Needs Repair. Fair does not count.
export const REPAIR_STATUSES = ['repair', 'must-id', 'deteriorated', 'replace', 'illumination', 'no', 'retrofit'];
export const slotHasRepair = (slot, item) => {
  if (!slot || isNotApplicable(slot)) return false;
  if (Object.values(slot.statuses || {}).some(v => REPAIR_STATUSES.includes(v))) return true;
  if (item?.panelTable) return (slot.details?.panels || []).some(p => p.condition === 'repair');
  return false;
};

// Auto-fill of the primary findings box after a status (or panel) change:
//   every line Good / Yes / N/A (item 7: every panel Good) and the box is empty
//     → "No deficiency observed at time of inspection."
//   any repair marked and the box still holds that text → box cleared
// Text the engineer typed is never changed.
const PASS_VALUES = ['good', 'yes', 'na'];
export const autoFindings = (slot, item) => {
  if (!item || item.perLineComments || item.noPhoto || item.findings === false) return slot;
  const current = (slot.photos?.[0]?.findings || '').trim();
  const isStandard = current === NO_DEFICIENCY_TEXT;

  let allPass;
  if (item.panelTable) {
    const panels = Array.isArray(slot.details?.panels) ? slot.details.panels : [];
    allPass = panels.length > 0 && panels.every(p => p.condition === 'good');
  } else {
    allPass = item.lines.length > 0 && item.lines.every(l => PASS_VALUES.includes(slot.statuses?.[l.id]));
  }

  if (allPass && current === '') return setFindingsAt(slot, 0, NO_DEFICIENCY_TEXT);
  if (slotHasRepair(slot, item) && isStandard) return setFindingsAt(slot, 0, '');
  return slot;
};

// Item 7 panel table (falls back to an empty list)
export const getPanels = (slots) => {
  const panels = slots?.item_07?.details?.panels;
  return Array.isArray(panels) ? panels : [];
};

// ─── Load / save ──────────────────────────────────────────────────────────────

// Returns { siteSystems, slots } — slots keyed for every item in the
// jurisdiction plus the lead photos. Missing slots come back empty.
export const loadReport = async (projectId, county) => {
  const snap = await getDoc(slotsRef(projectId));
  const data = snap.exists() ? snap.data() : {};
  const stored = data.slots || {};
  const keys = [...LEAD_ITEMS.map(l => l.key), ...getReportItems(county).map(i => i.key)];
  const slots = {};
  keys.forEach(k => { slots[k] = stored[k] ? normalizeSlot(stored[k]) : emptySlot(); });
  return { siteSystems: data.siteSystems || {}, slots };
};

const writeFields = async (projectId, fields) => {
  try {
    await updateDoc(slotsRef(projectId), fields);
  } catch (e) {
    if (e?.code !== 'not-found') throw e;
    // First write for this project — create the document, then apply the fields
    await setDoc(slotsRef(projectId), { slots: {}, siteSystems: {} });
    await updateDoc(slotsRef(projectId), fields);
  }
};

export const saveSlot = async (projectId, itemKey, slot, uid) => {
  const clean = normalizeSlot(slot);
  await writeFields(projectId, {
    [`slots.${itemKey}`]: { ...clean, updatedAt: serverTimestamp(), updatedBy: uid || null },
  });
  return clean;
};

export const clearSlot = async (projectId, itemKey) => {
  await writeFields(projectId, { [`slots.${itemKey}`]: deleteField() });
};

export const saveSiteSystems = async (projectId, siteSystems) => {
  await writeFields(projectId, { siteSystems });
};

// ─── Slot edits (pure — return a new slot, then pass it to saveSlot) ──────────

export const setPrimaryPhoto = (slot, photoId) => {
  const s = normalizeSlot(slot);
  const photos = [...s.photos];
  if (photos.length === 0) photos.push({ photoId, findings: '' });
  else photos[0] = { ...photos[0], photoId };
  return touched({ ...s, photos });
};

export const addExtraPhoto = (slot, photoId, findings = '') => {
  const s = normalizeSlot(slot);
  const photos = s.photos.length === 0 ? [{ photoId: null, findings: '' }] : [...s.photos];
  photos.push({ photoId, findings });
  return touched({ ...s, photos });
};

export const removePhotoAt = (slot, index) => {
  const s = normalizeSlot(slot);
  if (index === 0) {
    // Removing the primary keeps its findings box; the slot just loses its photo
    const photos = [...s.photos];
    if (photos[0]) photos[0] = { ...photos[0], photoId: null };
    return touched({ ...s, photos });
  }
  return touched({ ...s, photos: s.photos.filter((_, i) => i !== index) });
};

export const setFindingsAt = (slot, index, text) => {
  const s = normalizeSlot(slot);
  const photos = [...s.photos];
  while (photos.length <= index) photos.push({ photoId: null, findings: '' });
  photos[index] = { ...photos[index], findings: text };
  return touched({ ...s, photos });
};

export const setStatus = (slot, lineId, value) => {
  const s = normalizeSlot(slot);
  return touched({ ...s, statuses: { ...s.statuses, [lineId]: value } });
};

export const setLineComment = (slot, lineId, text) => {
  const s = normalizeSlot(slot);
  return touched({ ...s, lineComments: { ...s.lineComments, [lineId]: text } });
};

export const setDetail = (slot, key, value) => {
  const s = normalizeSlot(slot);
  return touched({ ...s, details: { ...s.details, [key]: value } });
};

export const setNotApplicable = (slot, reason, source = 'engineer') => {
  const s = normalizeSlot(slot);
  const on = !!(reason && reason.trim());
  return touched({ ...s, naReason: on ? reason : '', naSource: on ? source : '' });
};

export const confirmSlot = (slot, uid) => ({
  ...normalizeSlot(slot), review: 'confirmed', confirmedBy: uid || null, confirmedAt: new Date().toISOString(),
});

export const reopenSlot = (slot) => ({
  ...normalizeSlot(slot), review: 'edited', confirmedBy: null, confirmedAt: null,
});

// ─── Completeness checks ──────────────────────────────────────────────────────

// What still blocks confirming a slot. Empty list → can confirm.
export const slotProblems = (slot, item) => {
  const s = normalizeSlot(slot);
  const problems = [];
  if (item.findings === false) {                  // lead photos: photo only
    if (!s.photos[0]?.photoId) problems.push('Choose a photo.');
    return problems;
  }
  if (isNotApplicable(s)) return problems;
  if (!item.noPhoto && !s.photos[0]?.photoId) problems.push('Choose a photo or mark N/A.');
  const missing = item.lines.filter(l => !s.statuses[l.id]);
  if (missing.length) problems.push(`Set status: ${missing.map(l => l.label).join(', ')}.`);
  const unanswered = (item.details || []).filter(f => f.required && !String(s.details?.[f.key] ?? '').trim());
  if (unanswered.length) problems.push(`Answer: ${unanswered.map(f => f.label).join(' ')}`);
  // A repair needs at least one REPAIR photo (added with "+ Additional repair photo")
  if (slotHasRepair(s, item) && !item.noPhoto && !s.photos.slice(1).some(p => p.photoId)) {
    problems.push('Add at least one repair photo.');
  }
  if (item.panelTable) {
    const panels = Array.isArray(s.details?.panels) ? s.details.panels : [];
    if (panels.length === 0) problems.push('Add at least one panel.');
    else if (panels.some(p => !p.condition)) problems.push('Set Good or Needs Repair for every panel.');
    else if (panels.some(p => p.condition === 'repair') && !(s.photos[0]?.findings || '').trim()) {
      problems.push('A panel needs repair. Describe it in the findings box.');
    }
  }
  if (!item.perLineComments && !(s.photos[0]?.findings || '').trim() && !problems.some(x => x.includes('findings box'))) {
    problems.push('Enter findings.');
  }
  // A repair is marked but the findings still say there is no deficiency
  if (slotHasRepair(s, item) && /no deficienc(y|ies) observed/i.test(combinedFindings(s, item))) {
    problems.push(item.panelTable
      ? 'A panel needs repair but the findings say no deficiency. Describe the repair.'
      : 'A repair is marked but the findings say no deficiency. Describe the repair.');
  }
  return problems;
};
